import { useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import { colors, getSessionDrawerChrome, sessionDrawerLapTableShellSx, sessionDrawerLapToneStyles } from '@kangaroo/ui';

import type { SessionLapKind, SessionLapOption } from '../../features/sessions/sessionContent';
import { formatGap } from '../../lib/telemetrySession';

type SessionsLapTableProps = {
  laps: SessionLapOption[];
  selectedLapNumber: number | null;
  selectionMode?: 'primary' | 'compare';
  loading?: boolean;
  emptyLabel: string;
  footageCountsByLap?: Record<number, number>;
  onSelect: (lapNumber: number) => void;
};

export function SessionsLapTable({
  laps,
  selectedLapNumber,
  selectionMode = 'primary',
  loading = false,
  emptyLabel,
  footageCountsByLap = {},
  onSelect,
}: SessionsLapTableProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const chrome = getSessionDrawerChrome(isDark);
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const selectionTone = selectionMode === 'compare' ? '#F59E0B' : colors.primary[400];

  if (loading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (laps.length === 0) {
    return (
      <Typography sx={{ fontSize: '0.72rem', color: chrome.empty, py: 1 }}>
        {emptyLabel}
      </Typography>
    );
  }

  return (
    <Box sx={sessionDrawerLapTableShellSx(isDark)}>
      <TableContainer sx={{ maxHeight: 220, position: 'relative', zIndex: 0 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Lap', 'Time', 'Gap'].map((column) => (
                <TableCell
                  key={column}
                  align={column === 'Lap' ? 'left' : 'right'}
                  sx={{
                    py: 0.55,
                    fontSize: '0.62rem',
                    fontWeight: 800,
                    color: chrome.tableHeadText,
                    letterSpacing: '0.04em',
                    borderBottomColor: chrome.tableHeadBorder,
                    bgcolor: chrome.tableHeadBg,
                  }}
                >
                  {column}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {laps.map((lap) => {
              const rowKey = `${lap.kind}-${lap.lapNumber}-${lap.rowLabel}`;
              const isSelected = lap.selectable && lap.lapNumber === selectedLapNumber;
              const tone = sessionDrawerLapToneStyles(isDark, lap.kind, isSelected);
              const rowHovered = hoveredKey === rowKey;
              const footageCount = footageCountsByLap[lap.lapNumber] ?? 0;

              return (
                <TableRow
                  key={rowKey}
                  hover={lap.selectable}
                  selected={isSelected}
                  onMouseEnter={() => setHoveredKey(rowKey)}
                  onMouseLeave={() => setHoveredKey(null)}
                  onClick={() => {
                    if (lap.selectable) {
                      onSelect(lap.lapNumber);
                    }
                  }}
                  sx={{
                    cursor: lap.selectable ? 'pointer' : 'default',
                    opacity: lap.selectable ? 1 : 0.72,
                    bgcolor:
                      isSelected && selectionMode === 'compare'
                        ? chrome.lapCompareSelectedBg
                        : tone.bg,
                    '&.Mui-selected': {
                      bgcolor: tone.bg,
                    },
                    ...(lap.selectable && !isSelected && rowHovered
                      ? {
                          bgcolor:
                            selectionMode === 'compare'
                              ? chrome.lapCompareHoverBg
                              : chrome.lapPrimaryHoverBg,
                        }
                      : {}),
                  }}
                >
                  <TableCell
                    sx={{
                      py: 0.65,
                      fontSize: '0.78rem',
                      fontWeight: isSelected ? 800 : 650,
                      color: tone.color,
                      borderBottomColor: chrome.tableRowBorder,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                      {isSelected && lap.kind === 'flying' ? (
                        <StarIcon sx={{ fontSize: 15, color: selectionTone }} aria-hidden />
                      ) : null}
                      <span>{lap.rowLabel}</span>
                      {footageCount > 0 ? (
                        <Chip
                          size="small"
                          icon={<VideocamOutlinedIcon sx={{ fontSize: '12px !important' }} />}
                          label={footageCount > 1 ? String(footageCount) : ''}
                          aria-label={footageCount > 1 ? `${String(footageCount)} footage clips` : 'Footage available'}
                          sx={{
                            height: 18,
                            minWidth: footageCount > 1 ? 28 : 22,
                            ml: 0.15,
                            color: chrome.footageChipText,
                            bgcolor: chrome.footageChipBg,
                            border: `1px solid ${chrome.footageChipBorder}`,
                            '& .MuiChip-icon': {
                              ml: footageCount > 1 ? 0.45 : 0,
                              mr: footageCount > 1 ? -0.2 : 0,
                              color: chrome.footageChipIcon,
                            },
                            '& .MuiChip-label': {
                              px: footageCount > 1 ? 0.55 : 0,
                              fontSize: '0.62rem',
                              fontWeight: 900,
                            },
                          }}
                        />
                      ) : null}
                    </Box>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      py: 0.65,
                      fontSize: '0.78rem',
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                      fontVariantNumeric: 'tabular-nums',
                      color: chrome.lapTimeText,
                      borderBottomColor: chrome.tableRowBorder,
                    }}
                  >
                    {lap.label}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      py: 0.65,
                      fontSize: '0.72rem',
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                      fontVariantNumeric: 'tabular-nums',
                      color: chrome.lapGapText,
                      borderBottomColor: chrome.tableRowBorder,
                    }}
                  >
                    {lap.gapSec == null ? '—' : formatGap(lap.gapSec)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
